import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Package, X } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SEO } from "../components/SEO";
import { ProductCard } from "../components/ProductCard";
import { Toast } from "../components/Toast";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [subcategories, setSubcategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();
  const PRODUCTS_PER_PAGE = 24;

  const category = searchParams.get("category");
  const brand = searchParams.get("brand");
  const search = searchParams.get("search");
  const activeSubcategory = searchParams.get("subcategory");
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";

  const PRICE_BANDS = [
    { label: "Under £10", min: "", max: "10" },
    { label: "£10 - £20", min: "10", max: "20" },
    { label: "£20 - £50", min: "20", max: "50" },
    { label: "£50 - £100", min: "50", max: "100" },
    { label: "£100 - £250", min: "100", max: "250" },
    { label: "£250+", min: "250", max: "" },
  ];

  const setPriceBand = (min, max) => {
    const np = new URLSearchParams(searchParams);
    if (min) np.set("min_price", min); else np.delete("min_price");
    if (max) np.set("max_price", max); else np.delete("max_price");
    setSearchParams(np);
  };

  const clearPriceFilter = () => {
    const np = new URLSearchParams(searchParams);
    np.delete("min_price");
    np.delete("max_price");
    setSearchParams(np);
  };

  const isPriceBandActive = (b) => b.min === minPrice && b.max === maxPrice;

  // Fetch subcategories when category changes
  useEffect(() => {
    if (category) {
      axios.get(`${API}/categories/${encodeURIComponent(category)}/subcategories`)
        .then(res => setSubcategories(res.data || []))
        .catch(() => setSubcategories([]));
    } else {
      setSubcategories([]);
    }
  }, [category]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.append("category", category);
        if (brand) params.append("brand", brand);
        if (search) params.append("search", search);
        if (activeSubcategory) params.append("subcategory", activeSubcategory);
        if (minPrice) params.append("min_price", minPrice);
        if (maxPrice) params.append("max_price", maxPrice);
        params.append("skip", ((page - 1) * PRODUCTS_PER_PAGE).toString());
        params.append("limit", PRODUCTS_PER_PAGE.toString());
        
        const countParams = new URLSearchParams(
          Object.fromEntries([
            category && ["category", category],
            brand && ["brand", brand],
            search && ["search", search],
            activeSubcategory && ["subcategory", activeSubcategory],
            minPrice && ["min_price", minPrice],
            maxPrice && ["max_price", maxPrice],
          ].filter(Boolean))
        );

        const [productsRes, countRes] = await Promise.all([
          axios.get(`${API}/products?${params.toString()}`),
          axios.get(`${API}/products/count?${countParams.toString()}`)
        ]);
        setProducts(productsRes.data);
        setTotalProducts(countRes.data.count);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, brand, search, page, activeSubcategory, minPrice, maxPrice]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [category, brand, search, activeSubcategory, minPrice, maxPrice]);

  const handleAddToCart = async (productId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    const success = await addToCart(productId);
    if (success) {
      setToast({ message: "Added to cart!", type: "success" });
    }
  };

  const handleSubcategoryClick = (subcatName) => {
    const newParams = new URLSearchParams(searchParams);
    if (subcatName === activeSubcategory) {
      newParams.delete("subcategory");
    } else {
      newParams.set("subcategory", subcatName);
    }
    setSearchParams(newParams, { replace: true });
  };

  const clearSubcategory = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("subcategory");
    setSearchParams(newParams, { replace: true });
  };

  const title = search ? `Search: "${search}"` : category || brand || "All Products";
  const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO title={activeSubcategory ? `${activeSubcategory} - ${title}` : title} description={`Browse ${totalProducts} ${title.toLowerCase()} at Carepoint Health Supplies. Free next-day delivery on orders over £30.`} />
      <div className="breadcrumb mb-4" data-testid="breadcrumb">
        <Link to="/">Home</Link> / 
        {activeSubcategory ? (
          <>
            <Link to={`/products?category=${encodeURIComponent(category)}`} className="hover:text-[#00a99d]"> {category}</Link> / <span>{activeSubcategory}</span>
          </>
        ) : (
          <span> {title}</span>
        )}
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="products-title">
        {activeSubcategory || title}
      </h1>
      {activeSubcategory && (
        <p className="text-gray-500 text-sm mb-4">in {category}</p>
      )}

      {/* Subcategory filter chips */}
      {subcategories.length > 1 && (
        <div className="mb-6" data-testid="subcategory-filters">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={clearSubcategory}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !activeSubcategory 
                  ? "bg-[#00a99d] text-white shadow-md" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="subcategory-all"
            >
              All {category}
            </button>
            {subcategories.map(sub => (
              <button
                key={sub.name}
                onClick={() => handleSubcategoryClick(sub.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeSubcategory === sub.name 
                    ? "bg-[#00a99d] text-white shadow-md" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`subcategory-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {sub.name} <span className="opacity-60">({sub.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Price filter bands */}
      <div className="mb-6" data-testid="price-filters">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600 font-medium mr-1">Price:</span>
          <button
            onClick={clearPriceFilter}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              !minPrice && !maxPrice
                ? "bg-[#00a99d] text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            data-testid="price-band-any"
          >
            Any
          </button>
          {PRICE_BANDS.map((b) => (
            <button
              key={b.label}
              onClick={() => setPriceBand(b.min, b.max)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isPriceBandActive(b)
                  ? "bg-[#00a99d] text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid={`price-band-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {b.label}
            </button>
          ))}
          {(minPrice || maxPrice) && (
            <span className="inline-flex items-center gap-1 bg-[#00a99d]/10 text-[#00a99d] px-3 py-1 rounded-full text-sm font-medium ml-1">
              {minPrice && `£${minPrice}`}{minPrice && maxPrice ? " - " : ""}{maxPrice && `£${maxPrice}`}{!maxPrice && minPrice ? "+" : ""}
              <button onClick={clearPriceFilter} className="hover:bg-[#00a99d]/20 rounded-full p-0.5" data-testid="clear-price-filter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Active filter tag */}
      {activeSubcategory && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-500">Filtered by:</span>
          <span className="inline-flex items-center gap-1 bg-[#00a99d]/10 text-[#00a99d] px-3 py-1 rounded-full text-sm font-medium">
            {activeSubcategory}
            <button onClick={clearSubcategory} className="hover:bg-[#00a99d]/20 rounded-full p-0.5" data-testid="clear-subcategory">
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No products found</p>
          <Link to="/products" className="text-[#00a99d] hover:underline mt-2 inline-block">View all products</Link>
        </div>
      ) : (
        <>
          <p className="text-gray-500 mb-6">{totalProducts} products found (showing {(page-1)*PRODUCTS_PER_PAGE + 1}-{Math.min(page*PRODUCTS_PER_PAGE, totalProducts)})</p>
          <div className="products-grid" data-testid="products-list">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded ${page === pageNum ? 'bg-[#00a99d] text-white' : 'border hover:bg-gray-50'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {totalPages > 5 && page < totalPages - 2 && (
                <>
                  <span>...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-10 h-10 border rounded hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
